import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  CurrencyEuroIcon,
  TagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CubeIcon
} from '@heroicons/react/24/outline'
import { UPDATE_PRODUCT_VARIANT } from '../graphql/product-mutations'
import { GET_PRODUCT_VARIANT } from '../graphql/product-queries'
import { UpdateProductVariantData, ProductVariant } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import SizeSelect from '../components/SizeSelect'
import toast from 'react-hot-toast'

const EditVariantPage = () => {
  const { productId, variantId } = useParams<{ productId: string; variantId: string }>()
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState<UpdateProductVariantData>({
    name: '',
    price_cents: 0,
    sku: '',
    size: '',
    active: true
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data, loading: queryLoading, error: queryError } = useQuery(GET_PRODUCT_VARIANT, {
    variables: { id: parseInt(variantId!) },
    skip: !variantId
  })

  useEffect(() => {
    if (data?.productVariant) {
      const variant: ProductVariant = data.productVariant
      setFormData({
        name: variant.name,
        price_cents: variant.price_cents,
        deposit_cents: variant.deposit_cents ?? 0,
        sku: variant.sku || '',
        size: variant.size || '',
        active: variant.active
      })
    }
  }, [data])

  const [updateVariant, { loading: updateLoading }] = useMutation(UPDATE_PRODUCT_VARIANT, {
    onCompleted: () => {
      toast.success('Variant updated successfully!')
      navigate(`/products/${productId}/variants/${variantId}`)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update variant')
      if (error.message.includes('already exists')) {
        setErrors({ name: 'A variant with this name already exists for this product' })
      }
    }
  })

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Variant name is required'
    }

    if (formData.price_cents !== undefined && formData.price_cents <= 0) {
      newErrors.price_cents = 'Price must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    updateVariant({
      variables: {
        id: parseInt(variantId!),
        variantData: {
          ...formData,
          name: formData.name?.trim(),
          sku: formData.sku?.trim() || undefined,
          // '' clears the size server-side; send null so the intent is explicit.
          size: formData.size?.trim() ? formData.size.trim() : null
        }
      }
    })
  }

  const handleInputChange = (field: keyof UpdateProductVariantData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (queryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (queryError || !data?.productVariant) {
    return <Navigate to={`/products/${productId}`} replace />
  }

  const variant: ProductVariant = data.productVariant

  // Helper function to format price for display
  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  // Helper function to convert euros to cents
  const eurosToCents = (euros: string) => {
    const parsed = parseFloat(euros) || 0
    return Math.round(parsed * 100)
  }

  // Helper function to convert cents to euros for display
  const centsToEuros = (cents: number) => {
    return (cents / 100).toFixed(2)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Link
              to={`/products/${productId}/variants/${variantId}`}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">Edit Variant</h1>
              <p className="text-lg text-gray-600 mt-1">
                Updating <span className="font-semibold text-primary-600">{variant.name}</span>
              </p>
            </div>
          </div>

          {/* Product Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <CubeIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{variant.product?.name}</h3>
                <p className="text-sm text-gray-500 capitalize">
                  Original price: {formatPrice(variant.price_cents)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Variant Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <TagIcon className="h-5 w-5 mr-2 text-primary-600" />
                Variant Information
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Update the variant details
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Variant Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Variant Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                    errors.name 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="e.g., Small, Large, Red, Blue"
                />
                {errors.name && (
                  <div className="mt-2 flex items-center text-sm text-red-600">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Price */}
              <div>
                <label htmlFor="price_euros" className="block text-sm font-semibold text-gray-900 mb-2">
                  Price *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="price_euros"
                    min="0"
                    step="0.01"
                    value={centsToEuros(formData.price_cents || 0)}
                    onChange={(e) => handleInputChange('price_cents', eurosToCents(e.target.value))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.price_cents 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="0.00"
                  />
                </div>
                {errors.price_cents && (
                  <div className="mt-2 flex items-center text-sm text-red-600">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {errors.price_cents}
                  </div>
                )}
              </div>

              {/* Statiegeld (deposit) — charged ON TOP of the price, no BTW.
                  0 = no deposit, which is also how you switch it off. */}
              <div>
                <label htmlFor="deposit_euros" className="block text-sm font-semibold text-gray-900 mb-2">
                  Statiegeld
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="deposit_euros"
                    min="0"
                    step="0.01"
                    value={centsToEuros(formData.deposit_cents || 0)}
                    onChange={(e) => handleInputChange('deposit_cents', eurosToCents(e.target.value))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 hover:border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="0.00"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Charged on top of the price and shown separately on the receipt. No BTW.
                  Leave at €0.00 for products without statiegeld.
                </p>
              </div>

              {/* SKU */}
              <div>
                <label htmlFor="sku" className="block text-sm font-semibold text-gray-900 mb-2">
                  SKU (Optional)
                </label>
                <input
                  type="text"
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent hover:border-gray-400 transition-colors"
                  placeholder="e.g., PROD-SM-001"
                />
                <div className="mt-2 flex items-start">
                  <InformationCircleIcon className="h-4 w-4 text-blue-500 mr-1 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-500">
                    Stock keeping unit for inventory management.
                  </p>
                </div>
              </div>

              {/* Size */}
              <div>
                <label htmlFor="size" className="block text-sm font-semibold text-gray-900 mb-2">
                  Size (Optional)
                </label>
                <SizeSelect
                  id="size"
                  value={formData.size ?? ''}
                  onChange={(size) => handleInputChange('size', size)}
                  includeNone
                />
                <div className="mt-2 flex items-start">
                  <InformationCircleIcon className="h-4 w-4 text-blue-500 mr-1 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-500">
                    Used by the Curry Me Club upgrade reward (medium → large). A variant without a
                    size can never be upgraded.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status & Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
                Status & Settings
              </h2>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="active" className="text-sm font-semibold text-gray-900">
                    Make this variant available for sale
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <Link
              to={`/products/${productId}/variants/${variantId}`}
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={updateLoading || !formData.name?.trim() || (formData.price_cents !== undefined && formData.price_cents <= 0)}
              className="btn-primary flex items-center space-x-2"
            >
              {updateLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Updating Variant...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  <span>Update Variant</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditVariantPage
